import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Brush, Eraser, ZoomIn, ZoomOut, RotateCcw, Palette, CheckCircle, XCircle, AlertCircle, X, Maximize2, Move } from 'lucide-react';

const MIN_COMMENT_LENGTH = 5;

// Confirmation Dialog Component
const ConfirmDialog = ({ show, action, onConfirm, onCancel }) => {
  if (!show) return null;

  const isAccept = action === 'accept';
  const title = isAccept ? 'Accept Image' : 'Reject Image';
  const message = isAccept
    ? 'Are you sure you want to accept this image with the current mask?'
    : 'Are you sure you want to reject this image?';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          {isAccept ? (
            <CheckCircle className="w-6 h-6 text-green-600" />
          ) : (
            <XCircle className="w-6 h-6 text-red-600" />
          )}
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
        
        <p className="text-gray-600 mb-6">{message}</p>
        
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 text-white rounded-md ${
              isAccept
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {isAccept ? 'Accept (E)' : 'Reject (E)'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function MaskAnnotator({ 
  imageUrl,
  maskData = null,
  tag = "defect",
  tagColor = "#ef4444",
  review_lock_id,
  review_id,
  onComplete,
  containerMode = false
}) {
  // State management
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [brushSize, setBrushSize] = useState(20);
  const [isErasing, setIsErasing] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [maskColor, setMaskColor] = useState(tagColor);
  const [maskOpacity, setMaskOpacity] = useState(0.5);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [showBrush, setShowBrush] = useState(false);
  const [smoothing, setSmoothing] = useState(0.5);
  const [smoothedPos, setSmoothedPos] = useState({ x: 0, y: 0 });
  const [lastPoints, setLastPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canvasInitialized, setCanvasInitialized] = useState(false);
  const [generalComment, setGeneralComment] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [hasMaskPixels, setHasMaskPixels] = useState(false);
  const [maskEdited, setMaskEdited] = useState(false);
  const [originalMaskData, setOriginalMaskData] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [lastBrushPosition, setLastBrushPosition] = useState(null);
  const [lastBrushSize, setLastBrushSize] = useState(brushSize);
  const [undoStack, setUndoStack] = useState([]);
  const [currentDrawing, setCurrentDrawing] = useState(false);
  
  // Refs
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Check if mask has any pixels
  const checkMaskHasPixels = useCallback(() => {
    if (!maskCanvasRef.current) return false;
    
    const maskCtx = maskCanvasRef.current.getContext('2d');
    const imageData = maskCtx.getImageData(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
    
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] > 0) {
        return true;
      }
    }
    return false;
  }, []);

  // Update display with overlay (only visual, never modifies mask canvas)
  const updateDisplay = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    animationFrameRef.current = requestAnimationFrame(() => {
      if (!overlayCanvasRef.current || !maskCanvasRef.current) return;
      
      try {
        const overlayCanvas = overlayCanvasRef.current;
        const maskCanvas = maskCanvasRef.current;
        const overlayCtx = overlayCanvas.getContext('2d', { willReadFrequently: true });
        const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
        
        // Clear overlay only
        overlayCtx.clearRect(0, 0, overlayCanvas.width, overlayCanvas.height);
        
        // Get mask image data to check if there's anything to display
        const maskImageData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        let hasAnyPixels = false;
        
        // Check if mask has any non-transparent pixels
        for (let i = 3; i < maskImageData.data.length; i += 4) {
          if (maskImageData.data[i] > 0) {
            hasAnyPixels = true;
            break;
          }
        }
        
        if (hasAnyPixels) {
          // Parse color
          const r = parseInt(maskColor.slice(1, 3), 16);
          const g = parseInt(maskColor.slice(3, 5), 16);
          const b = parseInt(maskColor.slice(5, 7), 16);
          
          // Create colored overlay from mask data
          const overlayImageData = overlayCtx.createImageData(maskCanvas.width, maskCanvas.height);
          
          for (let i = 0; i < maskImageData.data.length; i += 4) {
            if (maskImageData.data[i + 3] > 0) {
              overlayImageData.data[i] = r;
              overlayImageData.data[i + 1] = g;
              overlayImageData.data[i + 2] = b;
              overlayImageData.data[i + 3] = Math.round(maskImageData.data[i + 3] * maskOpacity);
            }
          }
          
          // Put the colored overlay
          overlayCtx.putImageData(overlayImageData, 0, 0);
        }
        
        // Update mask pixel status
        setHasMaskPixels(hasAnyPixels);
        
        // Check if mask has been edited
        if (originalMaskData && maskCanvas) {
          const currentMaskData = maskCanvas.toDataURL('image/png');
          setMaskEdited(currentMaskData !== originalMaskData);
        }
      } catch (err) {
        console.error('Display update error:', err);
      }
    });
  }, [maskColor, maskOpacity, originalMaskData]);

  // Save current mask state to undo stack
  const saveToUndoStack = useCallback(() => {
    if (!maskCanvasRef.current) return;
    
    try {
      const maskDataUrl = maskCanvasRef.current.toDataURL('image/png');
      setUndoStack(prev => {
        const newStack = [...prev, maskDataUrl];
        // Keep only last 10 states
        return newStack.slice(-10);
      });
    } catch (err) {
      console.error('Error saving to undo stack:', err);
    }
  }, []);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !maskCanvasRef.current) return;
    
    try {
      const maskCtx = maskCanvasRef.current.getContext('2d');
      const previousState = undoStack[undoStack.length - 1];
      
      // Remove last state from stack
      setUndoStack(prev => prev.slice(0, -1));
      
      // If there's a previous state, restore it
      if (previousState) {
        const img = new Image();
        img.onload = () => {
          maskCtx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
          maskCtx.drawImage(img, 0, 0);
          updateDisplay();
        };
        img.src = previousState;
      } else {
        // No previous state, clear the mask
        maskCtx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
        updateDisplay();
      }
    } catch (err) {
      console.error('Error during undo:', err);
      setError('Failed to undo action');
    }
  }, [undoStack, updateDisplay]);

  // Reset view to fit image
  const resetView = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const canvasWidth = canvasRef.current.width;
    const canvasHeight = canvasRef.current.height;
    
    if (canvasWidth === 0 || canvasHeight === 0) return;
    
    const scaleX = containerRect.width / canvasWidth;
    const scaleY = containerRect.height / canvasHeight;
    const scale = Math.min(scaleX, scaleY, 1) * 0.9;
    
    setZoom(scale);
    setPan({
      x: (containerRect.width - canvasWidth * scale) / 2,
      y: (containerRect.height - canvasHeight * scale) / 2
    });
  }, []);

  // Initialize canvases
  const initializeCanvases = useCallback(async (bgImg, maskDataUrl) => {
    console.log('initializeCanvases called', {
      hasImage: !!bgImg,
      hasMaskData: !!maskDataUrl,
      refs: {
        canvas: !!canvasRef.current,
        mask: !!maskCanvasRef.current,
        overlay: !!overlayCanvasRef.current
      }
    });
    
    if (!bgImg) {
      console.log('No background image provided');
      return;
    }
    
    // Wait for refs if needed
    let retries = 0;
    while ((!canvasRef.current || !maskCanvasRef.current || !overlayCanvasRef.current) && retries < 10) {
      console.log(`Waiting for canvas refs... attempt ${retries + 1}`);
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    
    if (!canvasRef.current || !maskCanvasRef.current || !overlayCanvasRef.current) {
      console.error('Canvas refs not available after waiting');
      setError('Failed to initialize canvas - please refresh the page');
      return;
    }
    
    console.log('All canvas refs available, proceeding with initialization');
    
    try {
      const canvas = canvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      const overlayCanvas = overlayCanvasRef.current;
      
      // Set canvas dimensions
      canvas.width = bgImg.width;
      canvas.height = bgImg.height;
      maskCanvas.width = bgImg.width;
      maskCanvas.height = bgImg.height;
      overlayCanvas.width = bgImg.width;
      overlayCanvas.height = bgImg.height;
      
      console.log('Canvas dimensions set:', { width: bgImg.width, height: bgImg.height });
      
      // Draw background
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(bgImg, 0, 0);
      
      // Initialize mask
      const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });
      maskCtx.clearRect(0, 0, bgImg.width, bgImg.height);
      
      if (maskDataUrl) {
        console.log('Loading initial mask...');
        await new Promise((resolve, reject) => {
          const maskImg = new Image();
          maskImg.crossOrigin = 'anonymous';
          maskImg.onload = () => {
            console.log('Mask image loaded, drawing to canvas');
            // Draw mask at proper size
            maskCtx.globalCompositeOperation = 'source-over';
            maskCtx.drawImage(maskImg, 0, 0, bgImg.width, bgImg.height);
            
            // Convert to binary mask
            const imageData = maskCtx.getImageData(0, 0, bgImg.width, bgImg.height);
            const data = imageData.data;
            
            for (let i = 0; i < data.length; i += 4) {
              const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
              if (brightness > 128) {
                data[i] = 255;
                data[i + 1] = 255;
                data[i + 2] = 255;
                data[i + 3] = 255;
              } else {
                data[i] = 0;
                data[i + 1] = 0;
                data[i + 2] = 0;
                data[i + 3] = 0;
              }
            }
            
            maskCtx.putImageData(imageData, 0, 0);
            console.log('Mask processed and applied');
            resolve();
          };
          maskImg.onerror = (err) => {
            console.error('Failed to load mask image:', err);
            resolve(); // Continue without mask
          };
          maskImg.src = maskDataUrl;
        });
      }
      
      // Store original mask data
      setTimeout(() => {
        if (maskCanvas) {
          setOriginalMaskData(maskCanvas.toDataURL('image/png'));
          console.log('Original mask data stored');
        }
        setCanvasInitialized(true);
        resetView();
        updateDisplay();
        console.log('Canvas initialization complete');
      }, 100);
      
    } catch (err) {
      setError('Error initializing canvases: ' + err.message);
      console.error('Canvas initialization error:', err);
    }
  }, [resetView, updateDisplay]);

  // Load image from URL
  const loadImageFromUrl = useCallback(async (url) => {
    if (!url) return;
    
    console.log('Loading background image');
    
    setIsLoading(true);
    setError(null);
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          console.log('Background image loaded', { width: img.width, height: img.height });
          resolve();
        };
        img.onerror = () => reject(new Error('Failed to load background image'));
        img.src = url;
      });
      
      setBackgroundImage(img);
      
    } catch (err) {
      setError(`Error loading image: ${err.message}`);
      console.error('Error loading image:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load image when URL changes
  useEffect(() => {
    if (imageUrl) {
      loadImageFromUrl(imageUrl);
    }
  }, [imageUrl, loadImageFromUrl]);

  // Initialize canvases when image is loaded
  useEffect(() => {
    if (backgroundImage && !isLoading && !canvasInitialized) {
      console.log('Background image ready, preparing to initialize canvases');
      
      // Extract mask data URL from maskData prop
      let maskDataUrl = null;
      if (maskData) {
        if (typeof maskData === 'string') {
          maskDataUrl = maskData;
        } else if (Array.isArray(maskData) && maskData.length > 0) {
          maskDataUrl = maskData[0].mask;
          if (maskData[0].comment) {
            setGeneralComment(maskData[0].comment);
          }
        } else if (maskData.mask) {
          maskDataUrl = maskData.mask;
          if (maskData.comment) {
            setGeneralComment(maskData.comment);
          }
        }
      }
      
      // Delay initialization to ensure refs are ready
      const initTimer = setTimeout(() => {
        if (canvasRef.current && maskCanvasRef.current && overlayCanvasRef.current) {
          console.log('Canvas refs ready, initializing now');
          initializeCanvases(backgroundImage, maskDataUrl);
        } else {
          console.log('Canvas refs still not ready:', {
            canvas: !!canvasRef.current,
            mask: !!maskCanvasRef.current,
            overlay: !!overlayCanvasRef.current
          });
        }
      }, 100);
      
      return () => clearTimeout(initTimer);
    }
    // Remove initializeCanvases from dependencies to prevent re-initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundImage, maskData, isLoading, canvasInitialized]);

  // Update display when mask properties change (but not on initial load)
  useEffect(() => {
    if (canvasInitialized) {
      updateDisplay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskColor, maskOpacity]);

  // Validation logic
  const canAccept = !maskEdited;
  const canReject = hasMaskPixels && (maskEdited || generalComment.trim().length >= MIN_COMMENT_LENGTH);

  // Handle Accept/Reject
  const handleAccept = useCallback(() => {
    setConfirmAction('accept');
    setShowConfirmDialog(true);
  }, []);

  const handleReject = useCallback(() => {
    setConfirmAction('reject');
    setShowConfirmDialog(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!maskCanvasRef.current) return;
    
    const maskDataUrl = maskCanvasRef.current.toDataURL('image/png');
    
    const result = {
      status: confirmAction === 'accept' ? 'accepted' : 'rejected',
      review_lock_id,
      review_id,
      comment: generalComment.trim() || undefined,
      masks_data: {
        mask: maskDataUrl,
        tag: tag,
        comment: generalComment.trim() || undefined
      }
    };

    setShowConfirmDialog(false);
    if (onComplete) {
      onComplete(result);
    } else {
      console.log('Review result:', result);
    }
  }, [confirmAction, review_lock_id, review_id, generalComment, tag, onComplete]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in textarea or input
      if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') {
        return;
      }
      
      if (e.key === 'x' || e.key === 'X') {
        setIsErasing(prev => !prev);
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPanning(true);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      } else if (e.key === 'q' || e.key === 'Q') {
        e.preventDefault();
        // If dialog is open, do nothing (let 'e' handle confirmation)
        if (showConfirmDialog) return;
        
        // Trigger the active action (accept or reject based on conditions)
        if (canAccept) {
          handleAccept();
        } else if (canReject) {
          handleReject();
        }
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        // If dialog is open, confirm the action
        if (showConfirmDialog) {
          handleConfirm();
        }
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === ' ') {
        e.preventDefault();
        setIsPanning(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleUndo, showConfirmDialog, canAccept, canReject, handleAccept, handleReject, handleConfirm]);

  // Handle mouse wheel for zoom
  useEffect(() => {
    const handleWheel = (e) => {
      if (!containerRef.current) return;
      
      e.preventDefault();
      
      if (e.ctrlKey || e.metaKey) {
        // Zoom at cursor position
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newZoom = Math.max(0.1, Math.min(5, zoom * delta));
        
        // Calculate new pan to keep point under cursor
        const scaleChange = newZoom / zoom;
        const newPanX = x - (x - pan.x) * scaleChange;
        const newPanY = y - (y - pan.y) * scaleChange;
        
        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
      } else if (!isDrawing) {
        // Adjust brush size
        setBrushSize(prev => Math.max(5, Math.min(200, prev - e.deltaY * 0.1)));
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [isDrawing, zoom, pan]);

  // Get mouse position relative to canvas
  const getMousePos = useCallback((e) => {
    if (!containerRef.current || !canvasRef.current) return { x: 0, y: 0 };
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerX = e.clientX - containerRect.left;
    const containerY = e.clientY - containerRect.top;
    
    return {
      x: (containerX - pan.x) / zoom,
      y: (containerY - pan.y) / zoom
    };
  }, [pan, zoom]);

  // Draw at position
  const drawAtPosition = useCallback((x, y) => {
    if (!maskCanvasRef.current) return;
    
    try {
      const maskCtx = maskCanvasRef.current.getContext('2d', { willReadFrequently: true });
      
      // IMPORTANT: Preserve existing context state
      maskCtx.save();
      maskCtx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
      maskCtx.fillStyle = 'white';
      maskCtx.beginPath();
      maskCtx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
      maskCtx.fill();
      maskCtx.restore();
      
      updateDisplay();
    } catch (err) {
      console.error('Drawing error:', err);
    }
  }, [brushSize, isErasing, updateDisplay]);

  // Draw line between two points (for shift-click)
  const drawLine = useCallback((x1, y1, size1, x2, y2, size2) => {
    if (!maskCanvasRef.current) return;
    
    try {
      const maskCtx = maskCanvasRef.current.getContext('2d', { willReadFrequently: true });
      
      // Calculate distance and number of points to draw
      const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
      const minSize = Math.min(size1, size2);
      const steps = Math.ceil(distance / (minSize / 4)); // More steps for smoother line
      
      maskCtx.save();
      maskCtx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
      maskCtx.fillStyle = 'white';
      
      // Draw circles along the line with interpolated sizes
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const x = x1 + (x2 - x1) * t;
        const y = y1 + (y2 - y1) * t;
        // Interpolate brush size from start to end
        const currentSize = size1 + (size2 - size1) * t;
        
        maskCtx.beginPath();
        maskCtx.arc(x, y, currentSize / 2, 0, Math.PI * 2);
        maskCtx.fill();
      }
      
      maskCtx.restore();
      
      // Record the end position and size as last brush state
      setLastBrushPosition({ x: x2, y: y2 });
      setLastBrushSize(size2);
      
      updateDisplay();
    } catch (err) {
      console.error('Line drawing error:', err);
    }
  }, [isErasing, updateDisplay]);

  // Smooth drawing
  const smoothDraw = useCallback((currentPos) => {
    if (!isDrawing || !maskCanvasRef.current) return;
    
    const newPoints = [...lastPoints, currentPos].slice(-10);
    setLastPoints(newPoints);
    
    if (smoothing === 0 || newPoints.length === 1) {
      drawAtPosition(currentPos.x, currentPos.y);
    } else {
      let avgX = 0, avgY = 0, totalWeight = 0;
      
      newPoints.forEach((point, index) => {
        const weight = (index + 1) / newPoints.length;
        avgX += point.x * weight;
        avgY += point.y * weight;
        totalWeight += weight;
      });
      
      avgX /= totalWeight;
      avgY /= totalWeight;
      
      const targetX = smoothedPos.x + (avgX - smoothedPos.x) * (1 - smoothing);
      const targetY = smoothedPos.y + (avgY - smoothedPos.y) * (1 - smoothing);
      
      setSmoothedPos({ x: targetX, y: targetY });
      
      try {
        const maskCtx = maskCanvasRef.current.getContext('2d', { willReadFrequently: true });
        
        // IMPORTANT: Preserve existing context state
        maskCtx.save();
        maskCtx.globalCompositeOperation = isErasing ? 'destination-out' : 'source-over';
        maskCtx.strokeStyle = 'white';
        maskCtx.lineWidth = brushSize;
        maskCtx.lineCap = 'round';
        maskCtx.lineJoin = 'round';
        maskCtx.beginPath();
        maskCtx.moveTo(smoothedPos.x, smoothedPos.y);
        maskCtx.lineTo(targetX, targetY);
        maskCtx.stroke();
        maskCtx.restore();
        
        updateDisplay();
      } catch (err) {
        console.error('Smooth drawing error:', err);
      }
    }
  }, [isDrawing, lastPoints, smoothing, smoothedPos, isErasing, brushSize, drawAtPosition, updateDisplay]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    // Handle shift-click for line drawing
    if (e.shiftKey && e.button === 0) {
      const pos = getMousePos(e);
      
      // Save state before drawing line
      saveToUndoStack();
      
      if (lastBrushPosition && lastBrushSize !== null) {
        // Draw line from last position to current position with size interpolation
        drawLine(lastBrushPosition.x, lastBrushPosition.y, lastBrushSize, pos.x, pos.y, brushSize);
      } else {
        // No previous position, just draw at current position
        drawAtPosition(pos.x, pos.y);
      }
      return;
    }
    
    if (e.button === 0) {
      if (isPanning) {
        setIsPanning(true);
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      } else {
        // Save state before starting to draw
        saveToUndoStack();
        setIsDrawing(true);
        setCurrentDrawing(true);
        const pos = getMousePos(e);
        setSmoothedPos(pos);
        setLastPoints([pos]);
        drawAtPosition(pos.x, pos.y);
      }
    } else if (e.button === 1) {
      // Middle mouse button for panning
      e.preventDefault();
      setIsPanning(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [isPanning, pan, getMousePos, drawAtPosition, lastBrushPosition, lastBrushSize, brushSize, drawLine, saveToUndoStack]);

  const handleMouseMove = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
    
    if (isPanning && dragStart) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    } else if (isDrawing) {
      const pos = getMousePos(e);
      smoothDraw(pos);
    }
  }, [isPanning, dragStart, isDrawing, getMousePos, smoothDraw]);

  const handleMouseUp = useCallback((e) => {
    // Record the last position and brush size on mouse release
    if (isDrawing) {
      const pos = getMousePos(e);
      setLastBrushPosition(pos);
      setLastBrushSize(brushSize);
    }
    
    setIsDrawing(false);
    setCurrentDrawing(false);
    setIsPanning(false);
    setDragStart(null);
    setLastPoints([]);
  }, [isDrawing, getMousePos, brushSize]);

const handleMouseEnter = useCallback(() => {
    setShowBrush(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowBrush(false);
    handleMouseUp();
  }, [handleMouseUp]);

  // Check if mouse is over container on mount/update
  useEffect(() => {
    if (!containerRef.current) return;
    
    const checkMousePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      
      // Get current mouse position from last event or check if mouse is over element
      const handleGlobalMouseMove = (e) => {
        const isOver = e.clientX >= rect.left && 
                       e.clientX <= rect.right && 
                       e.clientY >= rect.top && 
                       e.clientY <= rect.bottom;
        setShowBrush(isOver);
      };
      
      window.addEventListener('mousemove', handleGlobalMouseMove, { once: true });
    };
    
    // Small delay to ensure container is rendered
    const timer = setTimeout(checkMousePosition, 100);
    return () => clearTimeout(timer);
  }, [canvasInitialized]);

  // Clear mask
  const clearMask = useCallback(() => {
    if (!maskCanvasRef.current) return;
    try {
      // Save state before clearing
      saveToUndoStack();
      
      const maskCtx = maskCanvasRef.current.getContext('2d');
      maskCtx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
      setLastBrushPosition(null); // Reset last brush position
      updateDisplay();
    } catch (err) {
      setError('Error clearing mask: ' + err.message);
    }
  }, [updateDisplay, saveToUndoStack]);

  // Zoom controls
  const handleZoomIn = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const newZoom = Math.min(5, zoom * 1.2);
    const scaleChange = newZoom / zoom;
    
    setPan({
      x: centerX - (centerX - pan.x) * scaleChange,
      y: centerY - (centerY - pan.y) * scaleChange
    });
    setZoom(newZoom);
  };
  
  const handleZoomOut = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    const newZoom = Math.max(0.1, zoom / 1.2);
    const scaleChange = newZoom / zoom;
    
    setPan({
      x: centerX - (centerX - pan.x) * scaleChange,
      y: centerY - (centerY - pan.y) * scaleChange
    });
    setZoom(newZoom);
  };

  // Get cursor style
  const getCursorStyle = () => {
    if (isPanning) return 'grabbing';
    if (isDrawing) return 'crosshair';
    return 'crosshair';
  };

  return (
    <div className={`${containerMode ? 'h-full' : 'h-screen'} bg-gray-50 flex flex-col overflow-hidden`}>
      {/* Error display */}
      {error && (
        <div className="bg-red-900/50 text-white p-2 flex items-center gap-2">
          <AlertCircle size={16} />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">Mask Annotation Review</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tag:</span>
            <span
              className="px-3 py-1 text-sm font-medium text-white rounded"
              style={{ backgroundColor: maskColor }}
            >
              {tag.charAt(0).toUpperCase() + tag.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Interface */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 flex flex-col bg-white border-r overflow-hidden">
          {/* Toolbar */}
          <div className="bg-gray-50 px-4 py-3 flex items-center gap-4 border-b flex-wrap">
            <button
              onClick={() => setIsErasing(false)}
              className={`p-2 rounded transition-colors ${
                !isErasing ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              title="Brush (Add to mask)"
            >
              <Brush size={18} />
            </button>
            
            <button
              onClick={() => setIsErasing(true)}
              className={`p-2 rounded transition-colors ${
                isErasing ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              title="Eraser (Remove from mask)"
            >
              <Eraser size={18} />
            </button>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Size:</label>
              <input
                type="range"
                min="5"
                max="200"
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm w-12 text-gray-600">{brushSize}px</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Palette size={18} className="text-gray-600" />
              <input
                type="color"
                value={maskColor}
                onChange={(e) => setMaskColor(e.target.value)}
                className="w-10 h-8 rounded cursor-pointer border"
                title="Mask Color"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Opacity:</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={maskOpacity}
                onChange={(e) => setMaskOpacity(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-sm w-12 text-gray-600">{Math.round(maskOpacity * 100)}%</span>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Smoothing:</label>
              <input
                type="range"
                min="0"
                max="0.95"
                step="0.05"
                value={smoothing}
                onChange={(e) => setSmoothing(Number(e.target.value))}
                className="w-24"
              />
              <span className="text-sm w-12 text-gray-600">{Math.round(smoothing * 100)}%</span>
            </div>
            
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={handleZoomOut}
                className="p-2 rounded bg-white hover:bg-gray-100 transition-colors border"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              
              <span className="text-sm w-16 text-center text-gray-600">{Math.round(zoom * 100)}%</span>
              
              <button
                onClick={handleZoomIn}
                className="p-2 rounded bg-white hover:bg-gray-100 transition-colors border"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
              
              <button
                onClick={resetView}
                className="p-2 rounded bg-white hover:bg-gray-100 transition-colors border"
                title="Reset View"
              >
                <Maximize2 size={18} />
              </button>
            </div>
            
            <button
              onClick={clearMask}
              className="px-4 py-2 bg-white hover:bg-gray-100 rounded transition-colors border text-sm"
              disabled={!backgroundImage}
            >
              Clear Mask
            </button>
            
            <button
              onClick={handleUndo}
              className="px-4 py-2 bg-white hover:bg-gray-100 rounded transition-colors border text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={undoStack.length === 0}
              title="Undo (Ctrl+Z)"
            >
              Undo ({undoStack.length}/10)
            </button>
          </div>

          {/* Canvas */}
          <div
            ref={containerRef}
            className="flex-1 overflow-hidden bg-gray-100 relative"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onContextMenu={(e) => e.preventDefault()}
            style={{ cursor: getCursorStyle() }}
          >
            {/* Always render canvases but control visibility */}
            <div
              className="absolute"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'top left',
                willChange: 'transform',
                transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                visibility: backgroundImage && canvasInitialized ? 'visible' : 'hidden'
              }}
            >
              <canvas
                ref={canvasRef}
                className="absolute shadow-lg"
                style={{ 
                  imageRendering: zoom > 2 ? 'pixelated' : 'auto',
                  pointerEvents: 'none'
                }}
              />
              
              <canvas
                ref={overlayCanvasRef}
                className="absolute pointer-events-none"
                style={{ 
                  imageRendering: zoom > 2 ? 'pixelated' : 'auto',
                  mixBlendMode: 'normal'
                }}
              />
              
              <canvas
                ref={maskCanvasRef}
                className="hidden"
              />
            </div>
            
            {/* Show loading/empty state */}
            {(!backgroundImage || !canvasInitialized) && (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <p className="text-xl mb-2">{isLoading ? 'Loading image...' : 'Initializing canvas...'}</p>
                  <p className="text-sm">{isLoading ? 'Please wait' : 'Setting up drawing surface'}</p>
                </div>
              </div>
            )}
            
            {/* Shift-click first point indicator */}
            {lastBrushPosition && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: lastBrushPosition.x * zoom + pan.x + 'px',
                  top: lastBrushPosition.y * zoom + pan.y + 'px',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-lg opacity-70" />
              </div>
            )}
            
            {/* Brush preview */}
            {showBrush && backgroundImage && !isPanning && (
              <div
                className="absolute pointer-events-none border-2 border-white rounded-full opacity-50"
                style={{
                  width: brushSize * zoom + 'px',
                  height: brushSize * zoom + 'px',
                  left: mousePos.x - (brushSize * zoom) / 2 + 'px',
                  top: mousePos.y - (brushSize * zoom) / 2 + 'px',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                  backgroundColor: isErasing ? 'rgba(255,0,0,0.1)' : 'rgba(255,255,255,0.1)'
                }}
              />
            )}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                <div className="flex items-center gap-2 text-white">
                  <div className="animate-spin">⏳</div>
                  <span>Loading image...</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Instructions */}
          <div className="bg-gray-50 p-2 text-sm text-gray-600 border-t">
            <p>
              <strong>Controls:</strong> Q: Accept/Reject | E: Confirm | Click+Drag: Draw/Erase | Shift+Click: Line from last position | Ctrl+Z: Undo | Middle Mouse/Space+Drag: Pan | Scroll: Brush Size | Ctrl+Scroll: Zoom at cursor | X: Toggle Eraser
            </p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-900">Review Actions</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {/* Mask Status */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Mask Status</span>
                  <div className="flex gap-2">
                    <span className={`text-xs px-2 py-1 rounded ${
                      hasMaskPixels ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {hasMaskPixels ? 'Has Mask' : 'No Mask'}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      maskEdited ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {maskEdited ? 'Edited' : 'Unchanged'}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600">
                  {maskEdited 
                    ? 'Mask has been modified. Use Reject to save changes.' 
                    : 'Mask is unchanged. Use Accept to approve.'}
                </p>
              </div>

              {/* Comment Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (optional)
                </label>
                <textarea
                  value={generalComment}
                  onChange={(e) => setGeneralComment(e.target.value)}
                  placeholder="Add notes about this image..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {generalComment.trim().length > 0 && generalComment.trim().length < MIN_COMMENT_LENGTH && (
                  <p className="text-xs text-amber-600 mt-1">
                    At least {MIN_COMMENT_LENGTH} characters needed ({generalComment.trim().length}/{MIN_COMMENT_LENGTH})
                  </p>
                )}
              </div>

              {/* Instructions */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-sm font-medium text-blue-900 mb-2">Instructions</h3>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• <strong>Q:</strong> Accept/Reject (whichever is active)</li>
                  <li>• <strong>E:</strong> Confirm action in dialog</li>
                  <li>• <strong>Accept:</strong> Mask is correct as-is (no changes)</li>
                  <li>• <strong>Reject:</strong> Mask edited OR issues noted in comment</li>
                  <li>• <strong>Shift+Click:</strong> Draw line from last brush position (blue dot)</li>
                  <li>• Rejection requires an existing mask</li>
                  <li>• Edited mask will be saved with rejection</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t bg-gray-50 space-y-3">
            <button
              onClick={handleAccept}
              disabled={!canAccept}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Accept Image {canAccept && '(Q)'}
            </button>
            <button
              onClick={handleReject}
              disabled={!canReject}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <XCircle className="w-4 h-4" />
              Reject Image {canReject && '(Q)'}
            </button>
            {!canAccept && maskEdited && (
              <p className="text-xs text-amber-600 text-center">
                Mask has been edited - must reject to save changes
              </p>
            )}
            {!canReject && !hasMaskPixels && (
              <p className="text-xs text-gray-500 text-center">
                Rejection requires a mask with pixels
              </p>
            )}
            {!canReject && hasMaskPixels && !maskEdited && generalComment.trim().length < MIN_COMMENT_LENGTH && (
              <p className="text-xs text-gray-500 text-center">
                To reject: edit the mask or add comment ({MIN_COMMENT_LENGTH}+ chars)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        show={showConfirmDialog}
        action={confirmAction}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
}